import { Loader2 } from "lucide-react";
import { useState } from "react";

import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";

import { usePushRegistration } from "../notifications/use-push-registration";

/**
 * The register action on /e/{slug}: the button, plus the door-code prompt
 * when the event asks for one.
 *
 * The code is offered, never verified here. The registration trigger is the
 * only place that sees the stored code, and it fails the command with one
 * message for a wrong offering and for a missing one, so the refusal cannot be
 * used to feel the code out character by character. That an event asks for a
 * code is public either way: `registrationSecretEnabled` travels on the public
 * event document and is what renders this field. What the person reads is the
 * trigger's refusal restated by registrationFailureMessage in
 * command-feedback.ts, which keeps the single message.
 *
 * "Code" is the word on every string here. The stored field is
 * `registrationSecret` and the admin card calls itself the registration
 * secret, which is the schema's word rather than the invitee's.
 *
 * The input is capped at the 128 characters the rules bound the stored
 * secret to: a longer offering can only be a mistake.
 *
 * The click is also where push gets switched on by default. Browsers show
 * the notification prompt only in answer to a click, so the same click that
 * sends the registration asks for the permission (askOnRegister), and the
 * device registers for push once the person allows it. The registration
 * goes out first and never waits for that answer.
 */
export function RegisterControls({
  secretRequired,
  pending,
  onRegister,
}: {
  secretRequired: boolean;
  pending: boolean;
  onRegister: (registrationSecret?: string) => void;
}) {
  const [secret, setSecret] = useState("");
  const code = secret.trim();
  const { askOnRegister } = usePushRegistration();

  if (!secretRequired) {
    return (
      <Button
        variant="cta"
        size="lg"
        onClick={() => {
          onRegister();
          askOnRegister();
        }}
        disabled={pending}
        data-testid="register-button"
      >
        {pending && <Loader2 className="h-4 w-4 animate-spin" />}
        {pending ? "Registering..." : "Register for this event"}
      </Button>
    );
  }

  return (
    <Card className="max-w-md border-border bg-card text-card-foreground shadow-none dark:border-edge dark:border-lit-edge dark:shadow-lit">
      <CardContent className="space-y-4 pt-0">
        <div className="space-y-1.5">
          <Label htmlFor="registration-secret">Event code</Label>
          <Input
            id="registration-secret"
            testId="registration-secret"
            value={secret}
            placeholder="The code the organisers gave you"
            disabled={pending}
            maxLength={128}
            onChange={(e) => setSecret(e.target.value)}
          />
          <p className="text-sm text-muted-foreground">
            This event shares its code privately. Ask the organisers if you do
            not have it.
          </p>
        </div>
        <Button
          variant="cta"
          size="lg"
          className="w-full rounded-full font-bold"
          onClick={() => {
            onRegister(code);
            askOnRegister();
          }}
          disabled={pending || !code}
          data-testid="register-button"
        >
          {pending && <Loader2 className="h-4 w-4 animate-spin" />}
          {pending ? "Registering..." : "Register for this event"}
        </Button>
      </CardContent>
    </Card>
  );
}
